import { Icon } from '@iconify/react';

const Pagination = ({
    currentPage = 1,
    totalPages = 1,
    totalItems = 0,
    itemsPerPage = 10,
    onPageChange,
    onItemsPerPageChange,
    itemsPerPageOptions = [4, 8, 12, 20],
    showItemsPerPage = true,
    showInfo = true,
    itemLabel = 'items'
}) => {
    const startItem = totalItems > 0 ? ((currentPage - 1) * itemsPerPage) + 1 : 0;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    const handleFirstPage = () => {
        if (currentPage !== 1) {
            onPageChange(1);
        }
    };

    const handlePrevPage = () => {
        if (currentPage > 1) {
            onPageChange(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            onPageChange(currentPage + 1);
        }
    };

    const handleLastPage = () => {
        if (currentPage !== totalPages) {
            onPageChange(totalPages);
        }
    };

    const handleItemsPerPageChange = (e) => {
        const newItemsPerPage = Number(e.target.value);
        onItemsPerPageChange(newItemsPerPage);
    };

    // Generate page numbers to display
    const getPageNumbers = () => {
        const pages = [];
        for (let i = 1; i <= totalPages; i++) {
            if (totalPages <= 5) {
                pages.push(i);
            } else if (i === 1 || i === totalPages || Math.abs(i - currentPage) <= 1) {
                pages.push(i);
            }
        }
        return pages;
    };

    const pageNumbers = getPageNumbers();

    return (
        <div className="tw-bg-white tw-rounded-lg tw-py-3 tw-px-4 tw-shadow-sm tw-border tw-border-gray-100 tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-3">
            <div className="tw-flex tw-items-center tw-gap-2">
                {showItemsPerPage && (
                    <>
                        <span className="tw-text-sm tw-text-gray-600">Show</span>
                        <select
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                            className="tw-px-2 tw-py-1 tw-border tw-border-gray-200 tw-rounded tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-blue-500"
                        >
                            {itemsPerPageOptions.map(option => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </select>
                        <span className="tw-text-sm tw-text-gray-600">entries</span>
                    </>
                )}
                {showInfo && (
                    <span className="tw-text-sm tw-text-gray-400 tw-ml-2">
                        Showing {startItem} to {endItem} of {totalItems} {itemLabel}
                    </span>
                )}
            </div>
            <div className="tw-flex tw-items-center tw-gap-1">
                <button
                    onClick={handleFirstPage}
                    disabled={currentPage === 1}
                    className="tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-rounded tw-border tw-border-gray-200 hover:tw-bg-gray-50 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                    title="First Page"
                >
                    <Icon icon="mdi:chevron-double-left" className="tw-text-gray-600" />
                </button>
                <button
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                    className="tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-rounded tw-border tw-border-gray-200 hover:tw-bg-gray-50 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                    title="Previous Page"
                >
                    <Icon icon="mdi:chevron-left" className="tw-text-gray-600" />
                </button>
                {pageNumbers.map((page, index) => (
                    <span key={page} className="tw-flex tw-items-center">
                        {index > 0 && pageNumbers[index - 1] !== page - 1 && (
                            <span className="tw-px-1 tw-text-gray-400">...</span>
                        )}
                        <button
                            onClick={() => onPageChange(page)}
                            className={`tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-rounded tw-text-sm tw-font-medium ${
                                currentPage === page
                                    ? 'tw-bg-blue-600 tw-text-white'
                                    : 'tw-border tw-border-gray-200 tw-text-gray-600 hover:tw-bg-gray-50'
                            }`}
                        >
                            {page}
                        </button>
                    </span>
                ))}
                <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages || totalPages === 0}
                    className="tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-rounded tw-border tw-border-gray-200 hover:tw-bg-gray-50 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                    title="Next Page"
                >
                    <Icon icon="mdi:chevron-right" className="tw-text-gray-600" />
                </button>
                <button
                    onClick={handleLastPage}
                    disabled={currentPage === totalPages || totalPages === 0}
                    className="tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-rounded tw-border tw-border-gray-200 hover:tw-bg-gray-50 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                    title="Last Page"
                >
                    <Icon icon="mdi:chevron-double-right" className="tw-text-gray-600" />
                </button>
            </div>
        </div>
    );
};

export default Pagination;
